import { Spinner } from "@via/components/loading/loading.tsx";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import {
  useGetAIModel,
  useUndeployAIModel,
} from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import AutoDeployConfigModal from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/auto-deploy-config-modal.tsx";
import DeployModal from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/deploy-modal.tsx";
import TamarindInputSelector from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/tamarind-input-selector.tsx";
import {
  getModelRegistryName,
  getModelTypeName,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  AIModelDeployment,
  AIModelTamarindConfig,
  CortexRegistryType,
  DeployConfig,
  DeploymentStatus,
  GetAIModelDetailsResponse,
  VertexAIModelDeploymentConfig,
} from "common";
import { useEffect, useMemo, useState, type FC } from "react";
import { Tooltip } from "react-tooltip";

// Type guard to check if deployment is a full Model deployment
const isModelDeployment = (
  deployment: AIModelDeployment
): deployment is AIModelDeployment => {
  return deployment.deployable;
};

// Type guard to check if deployment is a simple Endpoint deployment
const isEndpointDeployment = (
  deployment: AIModelDeployment
): deployment is AIModelDeployment => {
  return !deployment.deployable && !!deployment.endpoint;
};

interface ModelProps {
  modelData: GetAIModelDetailsResponse;
  onDeploymentConfigChange: React.Dispatch<
    React.SetStateAction<VertexAIModelDeploymentConfig | null>
  >;
  deploymentConfig: VertexAIModelDeploymentConfig | null;
  onSelectionChange?: (selectedInputs: string[]) => void;
  initialSelectedInputs?: string[];
}

const POLLING_INTERVAL = 60000; // 1 minute

// Helper to format status for display
const formatStatus = (status: string): string => {
  switch (status) {
    case DeploymentStatus.ErrorDeploy:
      return "Deploy Error";
    case DeploymentStatus.ErrorUndeploy:
      return "Undeploy Error";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

const Model: FC<ModelProps> = ({
  modelData: initialModelData,
  onDeploymentConfigChange,
  deploymentConfig,
  onSelectionChange,
  initialSelectedInputs = [],
}) => {
  // Check if this is a Model type deployment (supports deploy/undeploy)
  const isModelTypeDeployment =
    initialModelData?.deployment &&
    isModelDeployment(initialModelData.deployment);

  // Check if this is a Tamarind model
  const isTamarindModel =
    initialModelData?.registry === CortexRegistryType.Tamarind;

  // Determine if we should poll based on deployment status
  const shouldPoll = useMemo(() => {
    if (!isModelTypeDeployment) return false;
    const status = initialModelData.deployment.deploymentStatus;
    return (
      status === DeploymentStatus.Deploying ||
      status === DeploymentStatus.Deployed ||
      status === DeploymentStatus.Undeploying
    );
  }, [initialModelData?.deployment, isModelTypeDeployment]);

  const [isDeployConfigModalOpen, setIsDeployConfigModalOpen] = useState(false);
  const [isAutoDeployConfigModalOpen, setIsAutoDeployConfigModalOpen] =
    useState(false);

  // Fetch AI model with polling when in transitional states
  const { data: polledModelData } = useGetAIModel(
    initialModelData?.id,
    shouldPoll,
    shouldPoll ? POLLING_INTERVAL : false
  );

  // Use polled data if available, otherwise use initial data
  const modelData = polledModelData || initialModelData;

  const { mutate: undeployAIModel, isPending: isUndeployingLoading } =
    useUndeployAIModel();

  if (!modelData) {
    return null;
  }

  const { config, deployment } = modelData;
  useEffect(() => {
    if (config) {
      // onDeploymentConfigChange(
      //   (config as VertexAIModelConfigModel)?.deploymentConfig
      // );
    }
  }, [config]);

  // Get model deployment data if it's a Model type
  const modelDeployment =
    deployment && isModelDeployment(deployment) ? deployment : null;

  // Get endpoint deployment data if it's an Endpoint type
  const endpointDeployment =
    deployment && isEndpointDeployment(deployment) ? deployment : null;

  const deploymentStatus = modelDeployment?.deploymentStatus;
  const isDeploying = deploymentStatus === DeploymentStatus.Deploying;
  const isDeployed = deploymentStatus === DeploymentStatus.Deployed;
  const isUndeploying = deploymentStatus === DeploymentStatus.Undeploying;
  const isUndeployed = deploymentStatus === DeploymentStatus.Undeployed;
  const isErrorDeploy = deploymentStatus === DeploymentStatus.ErrorDeploy;
  const isErrorUndeploy = deploymentStatus === DeploymentStatus.ErrorUndeploy;
  const hasError = isErrorDeploy || isErrorUndeploy;

  // Button states (only applicable for Model type deployments)
  // Deploy button: disabled when deploying, deployed, undeploying, or error undeploy (was deployed)
  const isDeployDisabled =
    !modelDeployment ||
    !isModelDeployment(modelDeployment) ||
    !modelDeployment.deployable ||
    isDeploying ||
    isDeployed ||
    isUndeploying ||
    isErrorUndeploy;

  // Undeploy button: disabled when deploying, undeploying, undeployed, no status, or error deploy (wasn't deployed)
  const isUndeployDisabled =
    !modelDeployment ||
    !isModelDeployment(modelDeployment) ||
    !modelDeployment.deployable ||
    isDeploying ||
    isUndeploying ||
    isUndeployed ||
    isErrorDeploy ||
    !deploymentStatus;

  const handleUndeploy = () => {
    if (modelData?.id) {
      undeployAIModel(modelData.id);
    }
  };

  // Determine if auto undeploy is enabled (from prop, default to false)
  // Handle auto deploy enabled toggle
  const handleAutoDeployToggle = (enabled: boolean) => {
    onDeploymentConfigChange(prev => {
      return {
        ...prev,
        autoDeployEnabled: enabled,
        autoUndeployEnabled: prev?.autoUndeployEnabled ?? false,
        autoDeployConfig:
          prev?.autoDeployEnabled === false
            ? undefined
            : prev?.autoDeployConfig,
      };
    });
  };

  // Handle auto undeploy enabled toggle
  const handleAutoUndeployToggle = (enabled: boolean) => {
    onDeploymentConfigChange(prev => {
      return {
        ...prev,
        autoUndeployEnabled: enabled,
        autoDeployEnabled: prev?.autoDeployEnabled ?? false,
        autoDeployConfig: prev?.autoDeployConfig ?? undefined,
      };
    });
  };

  // Handle auto deploy config change
  const handleAutoDeployConfigChange = (newConfig: DeployConfig | null) => {
    onDeploymentConfigChange(prev => {
      return {
        ...prev,
        autoDeployConfig: newConfig || undefined,
        autoDeployEnabled: prev?.autoDeployEnabled ?? false,
        autoUndeployEnabled: prev?.autoUndeployEnabled ?? false,
      };
    });
  };

  // Handle clearing auto deploy config
  const handleClearAutoDeployConfig = () => {
    onDeploymentConfigChange(prev => {
      return {
        ...prev,
        autoDeployConfig: undefined,
        autoDeployEnabled: prev?.autoDeployEnabled ?? false,
        autoUndeployEnabled: prev?.autoUndeployEnabled ?? false,
      };
    });
  };

  return (
    <div className="space-y-6 py-6">
      {/* Basic Information Section */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-gray-900">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Model Name
            </p>
            <p className="text-sm font-medium text-gray-900">
              {modelData?.name || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Model Type
            </p>
            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
              {getModelTypeName(modelData?.type)}
            </span>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Registry
            </p>
            <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800">
              {getModelRegistryName(modelData?.registry)}
            </span>
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      {config && "modelName" in config && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-gray-900">
            Model Configuration
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Config Model Name
              </p>
              <p className="text-sm font-medium text-gray-900">
                {(config.modelName as string) || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Version
              </p>
              <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-mono text-gray-800">
                {"version" in config ? (config.version as string) : "-"}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Launch Stage
              </p>
              <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-800">
                {"launchStage" in config ? config.launchStage : "-"}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Open Source Category
              </p>
              <p className="text-sm text-gray-900">
                {"openSourceCategory" in config
                  ? config.openSourceCategory
                  : "-"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tamarind Model Inputs Section */}
      {isTamarindModel && config && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-gray-900">
            Model Inputs
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Select optional inputs to automatically add them to the process.
            Required inputs are always included.
          </p>
          <TamarindInputSelector
            modelName={modelData.modelName}
            modelInputs={(config as AIModelTamarindConfig).settings || []}
            onSelectionChange={onSelectionChange}
            initialSelectedInputs={initialSelectedInputs}
          />
        </div>
      )}

      {/* Deployment Section - for Model type deployments (supports deploy/undeploy) */}
      {modelDeployment && isModelDeployment(modelDeployment) && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-gray-900">
            Deployment Configuration
          </h3>

          {/* Undeployed Banner */}
          {isUndeployed && (
            <div className="mb-4">
              <MessageBanner
                messages={[
                  "This deployment information belongs to the latest deployment which has been undeployed.",
                ]}
                type={BannerType.Info}
              />
            </div>
          )}

          {/* Error Banner */}
          {hasError && modelDeployment?.errorMessage && (
            <div className="mb-4">
              <MessageBanner
                messages={[
                  `${isErrorDeploy ? "Deployment" : "Undeployment"} failed: ${modelDeployment?.errorMessage}`,
                ]}
                type={BannerType.Error}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Deployable Status */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Deployable
              </p>
              <span
                className={classNames(
                  "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium",
                  modelDeployment.deployable
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                )}>
                {modelDeployment.deployable ? "✓ Yes" : "✗ No"}
              </span>
            </div>

            {/* Deployment Status */}
            {deploymentStatus && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Status
                </p>
                <div className="flex items-center gap-2">
                  <span
                    className={classNames(
                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium",
                      isDeployed
                        ? "bg-green-100 text-green-800"
                        : isDeploying || isUndeploying
                          ? "bg-yellow-100 text-yellow-800"
                          : hasError
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                    )}>
                    {(isDeploying || isUndeploying) && (
                      <Spinner loading={true} />
                    )}
                    {formatStatus(deploymentStatus)}
                  </span>
                </div>
              </div>
            )}

            {/* Deployed By */}
            {modelDeployment.deploymentStatus &&
              [
                DeploymentStatus.Deployed,
                DeploymentStatus.Deploying,
                DeploymentStatus.ErrorUndeploy,
                DeploymentStatus.ErrorDeploy,
              ].includes(modelDeployment.deploymentStatus) &&
              modelDeployment.deployedBy && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Deployed By
                  </p>
                  <span className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-700/10">
                    {modelDeployment.deployedBy}
                  </span>
                </div>
              )}

            {/* Undeployed By */}
            {modelDeployment.deploymentStatus &&
              [
                DeploymentStatus.Undeployed,
                DeploymentStatus.Undeploying,
              ].includes(modelDeployment.deploymentStatus) &&
              modelDeployment.undeployedBy && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Undeployed By
                  </p>
                  <span className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-700/10">
                    {modelDeployment.undeployedBy}
                  </span>
                </div>
              )}

            {/* Deployment Config */}
            {modelDeployment.deploymentConfig &&
              modelDeployment.deploymentConfig.autoDeployConfig && (
                <>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Machine Type
                    </p>
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                      {modelDeployment.deploymentConfig.autoDeployConfig
                        .machineType || "-"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Accelerator Type
                    </p>
                    <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                      {modelDeployment.deploymentConfig.autoDeployConfig
                        .acceleratorType || "-"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Accelerator Count
                    </p>
                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                      {modelDeployment.deploymentConfig.autoDeployConfig
                        .acceleratorCount ?? "-"}
                    </span>
                  </div>
                  {modelDeployment.deploymentConfig.autoDeployConfig
                    .containerImageURI && (
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Container Image
                      </p>
                      <span
                        id="container-image-uri"
                        className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-mono text-gray-700 ring-1 ring-inset ring-gray-700/10 max-w-full truncate cursor-help">
                        {
                          modelDeployment.deploymentConfig.autoDeployConfig
                            .containerImageURI
                        }
                      </span>
                      <Tooltip
                        anchorSelect="#container-image-uri"
                        content={
                          modelDeployment.deploymentConfig.autoDeployConfig
                            .containerImageURI
                        }
                        place="top"
                        className="z-50"
                      />
                      c
                    </div>
                  )}
                </>
              )}

            {/* Endpoint */}
            {modelDeployment.endpoint && (
              <div className="md:col-span-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Endpoint
                </p>
                <span
                  id="endpoint-uri"
                  className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-mono text-green-700 ring-1 ring-inset ring-green-700/10 max-w-full truncate cursor-help">
                  {modelDeployment.endpoint.dedicatedEndpointDns}
                </span>
                <Tooltip
                  anchorSelect="#endpoint-uri"
                  content={modelDeployment.endpoint.dedicatedEndpointDns}
                  place="top"
                  className="z-50"
                />
              </div>
            )}

            {/* Auto Deploy and Auto Undeploy Settings */}
            {modelDeployment?.deployable && (
              <div className="md:col-span-2 pt-4 border-t border-gray-100 space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                    Auto Deployment Settings
                  </p>

                  {/* Auto Deploy Checkbox */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deploymentConfig?.autoDeployEnabled ?? false}
                          onChange={e =>
                            handleAutoDeployToggle(e.target.checked)
                          }
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                        />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Auto Deploy
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Automatically deploy the model when the process
                            starts
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Auto Deploy Configuration - only show when enabled */}
                  {deploymentConfig?.autoDeployEnabled && (
                    <div className="ml-0 mb-4 pl-4 border-l-2 border-indigo-200">
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-gray-900">
                          Deployment Configuration
                        </h5>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Configuration is required when auto deploy is enabled
                        </p>
                      </div>

                      {deploymentConfig?.autoDeployConfig ? (
                        <>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 mb-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Machine Type
                              </p>
                              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                {deploymentConfig?.autoDeployConfig
                                  ?.machineType || "-"}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Accelerator Type
                              </p>
                              <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                                {deploymentConfig?.autoDeployConfig
                                  ?.acceleratorType || "-"}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Accelerator Count
                              </p>
                              <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                                {deploymentConfig?.autoDeployConfig
                                  ?.acceleratorCount ?? "-"}
                              </span>
                            </div>
                            {deploymentConfig?.autoDeployConfig
                              ?.containerImageURI && (
                              <div className="md:col-span-2 lg:col-span-4">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                  Container Image
                                </p>
                                <span
                                  id="auto-deploy-container-uri"
                                  className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-mono text-gray-700 ring-1 ring-inset ring-gray-700/10 max-w-full truncate cursor-help">
                                  {
                                    deploymentConfig?.autoDeployConfig
                                      ?.containerImageURI
                                  }
                                </span>
                                <Tooltip
                                  anchorSelect="#auto-deploy-container-uri"
                                  content={
                                    deploymentConfig?.autoDeployConfig
                                      ?.containerImageURI
                                  }
                                  place="top"
                                  className="z-50"
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setIsAutoDeployConfigModalOpen(true)
                              }
                              className="inline-flex items-center rounded-md bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                              Change Configuration
                            </button>
                            <button
                              type="button"
                              onClick={handleClearAutoDeployConfig}
                              className="inline-flex items-center rounded-md bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">
                              Clear
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 mb-3">
                            <p className="text-sm text-yellow-800">
                              <span className="font-semibold">
                                Configuration required:
                              </span>{" "}
                              Please set a deployment configuration to enable
                              auto deploy.
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setIsAutoDeployConfigModalOpen(true)
                              }
                              className="inline-flex items-center rounded-md bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                              Set Configuration
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Auto Undeploy Checkbox */}
                  <div className="flex items-start justify-between pt-2">
                    <div className="flex-1">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            deploymentConfig?.autoUndeployEnabled ?? false
                          }
                          onChange={e =>
                            handleAutoUndeployToggle(e.target.checked)
                          }
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                        />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Auto Undeploy
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Automatically undeploy the model when the process
                            completes
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deploy/Undeploy Buttons */}
            <div className="md:col-span-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Actions
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isDeployDisabled}
                  onClick={() => setIsDeployConfigModalOpen(true)}
                  className={classNames(
                    "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    isDeployDisabled
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600"
                  )}>
                  {isDeploying && <Spinner loading={true} />}
                  Deploy
                </button>
                <button
                  type="button"
                  onClick={handleUndeploy}
                  disabled={isUndeployDisabled || isUndeployingLoading}
                  className={classNames(
                    "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    isUndeployDisabled || isUndeployingLoading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600"
                  )}>
                  {(isUndeploying || isUndeployingLoading) && (
                    <Spinner loading={true} />
                  )}
                  Undeploy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Endpoint Section - for Endpoint type AI models (pre-configured endpoint) */}
      {endpointDeployment && isEndpointDeployment(endpointDeployment) && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-gray-900">
            Endpoint Configuration
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Endpoint ID
              </p>
              <span className="text-sm font-medium text-gray-900">
                {endpointDeployment.endpoint?.endpointId}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Endpoint
              </p>
              <span
                id="endpoint-uri-simple"
                className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-mono text-green-700 ring-1 ring-inset ring-green-700/10 max-w-full truncate cursor-help">
                {endpointDeployment.endpoint?.dedicatedEndpointDns}
              </span>
              <Tooltip
                anchorSelect="#endpoint-uri-simple"
                content={endpointDeployment.endpoint?.dedicatedEndpointDns}
                place="top"
                className="z-50"
              />
            </div>
          </div>
        </div>
      )}

      <DeployModal
        open={isDeployConfigModalOpen}
        setOpen={setIsDeployConfigModalOpen}
        aiModelId={modelData?.id}
      />

      <AutoDeployConfigModal
        open={isAutoDeployConfigModalOpen}
        setOpen={setIsAutoDeployConfigModalOpen}
        aiModelId={modelData?.id}
        currentConfig={deploymentConfig?.autoDeployConfig ?? null}
        onConfirm={newConfig => handleAutoDeployConfigChange(newConfig ?? null)}
      />
    </div>
  );
};

export default Model;
